/**
 * THE TECHNICIAN'S PATH — one panel, one thing at a time, start to finish.
 *
 * Anthony: "a separate path for technicians where they just click start
 * job and then it takes them through step by step in one panel to collect
 * all the information they need to fill out as they move through the job."
 *
 * WHY A SEPARATE PATH AT ALL. The job card is the right screen for the
 * desk: six tabs, every fact about the job, everything editable. It is the
 * wrong screen for somebody holding a phone in one hand under a car. A
 * technician does not need Money, Comms or History; they need to know what
 * the next thing they owe the job is, and a box to put it in. Given the
 * card, what actually happens is the photos get taken on the phone's own
 * camera roll and never arrive, and the diagnosis is told to the advisor
 * out loud and written down by nobody.
 *
 * THE STEPS ARE NOT INVENTED HERE. They are phases 8 to 15 of the process
 * on the Workflow page, which Anthony wrote and which the shop already
 * treats as the map. Two of those phases carry rules the database enforces
 * and this panel therefore enforces too:
 *
 *   - Phase 8: no authorization, no start.
 *   - Phase 15: no plate and no VIN, no finish.
 *
 * And phase 11 is the reason this is a funnel rather than a form. When the
 * repair turns out to be bigger than the figure the customer agreed to,
 * the technician's next step is NOT to carry on and tell somebody later.
 * It is to stop. So there is a step whose only outcome can be stopping,
 * and it sits between the diagnosis and the wrench.
 *
 * NOT LAUNCHED. Anthony: "Wait to launch this until we test this." It is
 * visible on staging and invisible everywhere else -- see techPathOn().
 * Nothing here needs a migration: the findings go on the job's notes with
 * a stamp, the photos through the existing pipeline, the plate and VIN
 * onto the vehicle. That is deliberate for a first look. Once the shape is
 * agreed, the diagnostic card and the odometer deserve real columns.
 * The diagnostic card got its column on 30 September (0188, and its own
 * screen at #/diagnosis). The odometer is still a stamp.
 */

import { isIdentified, identifyAsks, cap } from "../domain/trade.ts";
import { currentTrade, shopWords } from "../ui/words.ts";
import { reword } from "../domain/tracks.ts";
import {
  getJob, listLines, listAuthorizations, setStatus, updateJobField,
  updateVehicleField, attachVehicle, vehicleName, saveDiagnosis,
  type JobRow, type LineItem,
} from "../data/jobs.ts";
import { photosForJob, type PhotoKind } from "../data/photos.ts";
import { diagnosisOf, hasDiagnosis } from "../data/tests.ts";

/* Re-exported so the job card keeps one import for all of this. */
export { humanNotes } from "../data/tests.ts";
import {
  moneyFor, raiseInvoice, recordPayment, METHOD_LABEL, type Method,
} from "../data/payments.ts";
import { jobPhotos } from "./photos.ts";
import { authForm } from "./authorizations.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { db, api } from "../data/client.ts";
import type { Role } from "../domain/permissions.ts";

/**
 * WHO GETS THE PATH.
 *
 * Anthony: "let's push wizard to live version when ready... Service
 * advisors don't get wizard."
 *
 * It was gated on hostname while it was being tried out, which kept it off
 * production entirely. That gate is gone; this is a gate on the JOB, not
 * on the deployment.
 *
 * ADVISORS ARE EXCLUDED ON PURPOSE, and not because they are trusted less.
 * The path exists to walk somebody through work they are doing with their
 * hands, one step at a time, and it deliberately hides Money, Comms and
 * History to do it. An advisor needs all three at once and needs to jump
 * between them; handing them a funnel would be taking their screen away
 * and calling it help.
 *
 * Admin and supervisor keep it because they cover the floor, and because
 * the owner has to be able to see what his technicians see.
 */
/**
 * WHO GETS THE WALK.
 *
 * It started as the technician's path, and advisors were kept off it on
 * purpose: "Service advisors don't get wizard." That was right for a shop
 * with a desk and a yard, where the advisor prices and the technician
 * turns the wrench.
 *
 * It is wrong for most of the shops on this system. Anthony: "add wizard
 * from the beginning for them. For Matt being a solo shop owner operator.
 * He is both technician and owner basically for the flow." Matt Bolen IS
 * the desk and the yard, and so is nearly every shop that signs up. A path
 * that only exists for one of his two hats is a path he cannot use.
 *
 * Viewers are still out: they cannot change anything, so a walk that
 * refuses at every step would be a cruelty rather than a feature.
 */
const WALKS: Role[] = ["technician", "admin", "supervisor", "advisor"];

export const techPathOn = (role: Role): boolean => WALKS.includes(role);

/* ── the steps ────────────────────────────────────────────────────── */

type StepKey =
  | "start" | "inspect" | "found" | "money" | "repair" | "after" | "car"
  | "done" | "bill" | "paid";

type Step = {
  key: StepKey;
  /** What the technician is being asked for, in their words not ours. */
  title: string;
  /** The one line under it. Never two. */
  sub: string;
  /** Which phase of the shop's own process this is, so the two cannot drift. */
  phase: number;
  /** The photo kind this step files under, when it collects photos. */
  shoots?: PhotoKind;
};

const STEPS: Step[] = [
  /* THE PHOTO IS PART OF STARTING, NOT A STEP AFTER IT.
     Anthony: "Require one photo to start job, photo, move down to
     inspection then diagnostic."
     It was its own step called "Before you touch it", which made it
     something you got past rather than the condition of starting. A car
     whose arrival was never photographed is the car you cannot defend
     three months later, so it is now the thing that gates the start. */
  { key: "start", phase: 8, shoots: "arrival", title: "Start the job",
    sub: "The yes on file, the miles, and one photo of how it turned up." },
  { key: "inspect", phase: 9, title: "The inspection",
    sub: "Walk the car before anything comes apart." },
  { key: "found", phase: 10, shoots: "damage", title: "What you found",
    sub: "What is wrong with it, in your own words, and the photos." },
  { key: "money", phase: 11, title: "Is it bigger than the quote",
    sub: "If it is, you stop here. That is not a delay, it is the job." },
  { key: "repair", phase: 13, shoots: "part", title: "Do the repair",
    sub: "Keep shooting as you go. Nobody thinks to, everybody wants them later." },
  { key: "after", phase: 14, shoots: "release", title: "Photos after",
    sub: "The finished work, and the old part beside the new one." },
  { key: "car", phase: 15, title: "The car",
    sub: "A plate or a VIN. The job cannot close without one." },
  { key: "done", phase: 15, title: "Mark it finished",
    sub: "Everything the job needs is on it. The work is done." },

  /* PHASES 16 TO 18, AND THEY ARE NOT THE DESK'S BY RIGHT.
     Anthony: "if it's billing or invoicing they should be able to do this
     ... either advisor or tech basically should."
     He is right, and the permission model already agreed with him before
     this page existed: mayBill() asks what STATUS the job is at, never
     what role you are. The only thing a technician cannot do is dismiss a
     check, which is deliberate and stays. A mobile technician standing at
     a curb alone IS the whole shop for that job, and sending them back to
     an advisor to take a card is how money gets collected tomorrow. */
  { key: "bill", phase: 16, title: "Bill it",
    sub: "The total comes off the lines. Nobody types it." },
  { key: "paid", phase: 17, title: "Take the money",
    sub: "Card, cash, check or transfer. Then the keys go back." },
];

const stepAt = (k: StepKey): number => STEPS.findIndex((s) => s.key === k);

/**
 * THE STEPS THAT ARE NOT THE SAME WORDS IN EVERY SHOP.
 *
 * "Walk the car", "the miles", "the keys go back" and "A plate or a VIN"
 * describe things a garage door shop does not have. STEPS is read in half
 * a dozen places and changing its shape would touch all of them, so the
 * words are rewritten at render by reword() in tracks.ts, the same function
 * the job card and the advisor walk use, so the three cannot disagree.
 *
 * An auto shop gets the strings exactly as they are written above.
 */
const stepWords = (step: Step): Step => reword(step, currentTrade());

/**
 * THE ODOMETER BOX, ONLY FOR A TRADE THAT HAS ONE.
 *
 * Anthony, 2026-09-18: "for just their trade in shops."
 *
 * It was written out twice, ungated, so the walk asked a private
 * investigator and a locksmith to read an odometer off a case. The
 * vocabulary already answers this: words().reading is null for every trade
 * that does not measure one, and it is the same null the job card reads to
 * hide the field there.
 *
 * Returns "" rather than a disabled box. A field nobody can fill is worse
 * than no field: it reads as something the shop forgot to set up.
 */
function readingField(): string {
  const r = shopWords().reading;
  if (!r) return "";
  return `<label class="techfield">
      <span>${esc(cap(r))}, if you can read it</span>
      <input id="todo" inputmode="numeric" autocomplete="off">
    </label>`;
}

/**
 * FIVE NAMED STAGES OVER TEN STEPS.
 *
 * The rail was ten identical pips. Ten pips is a progress bar, not a map:
 * it says how far, never what is coming. Repair Ally's plan names five
 * stages and marks the one you are in, and that is the difference between
 * "four more of something" and "two more tests, then the repair".
 *
 * Grouped rather than renamed, because the steps are the shop's own
 * process and must keep their phase numbers.
 */
const STAGES: Array<{ name: string; keys: StepKey[] }> = [
  { name: "Check in", keys: ["start"] },
  { name: "Inspect", keys: ["inspect"] },
  { name: "Diagnose", keys: ["found", "money"] },
  { name: "Repair", keys: ["repair", "after"] },
  { name: "Close", keys: ["car", "done", "bill", "paid"] },
];

const stageOf = (k: StepKey): number =>
  Math.max(0, STAGES.findIndex((g) => g.keys.includes(k)));

/* ── where a job already is ───────────────────────────────────────── */

type Ready = {
  authorized: boolean;
  approvedFigure: number;
  priced: number;
  identified: boolean;
  shot: Record<string, number>;
  /** The technician has already written up what they found. */
  found: boolean;
};

/**
 * READ THE JOB, DO NOT ASK THE TECHNICIAN.
 *
 * A technician who took the arrival photos this morning, put the phone
 * down and came back after lunch must not be asked for them again. Every
 * step that can tell whether it is already done tells, from the job's own
 * record rather than from anything this panel remembers.
 */
async function readiness(job: JobRow, lines: LineItem[]): Promise<Ready> {
  const [auths, photos] = await Promise.all([
    listAuthorizations(job.id).catch(() => []),
    photosForJob(job.id).catch(() => []),
  ]);
  const live = auths.filter((a) => !a.declined);
  const shot: Record<string, number> = {};
  for (const p of photos) shot[p.kind] = (shot[p.kind] ?? 0) + 1;

  return {
    authorized: live.length > 0,
    approvedFigure: live.length ? Number(live[0]!.amount ?? 0) : 0,
    priced: lines.reduce((n, l) => n + Number(l.total ?? 0), 0),
    identified: isIdentified(currentTrade(), job.vehicles),
    shot,
    found: hasDiagnosis(job),
  };
}

/**
 * The first step that is not finished. A technician opening a job they
 * started yesterday lands where they left off rather than at the top.
 */
function firstUnfinished(job: JobRow, r: Ready): StepKey {
  if (!r.authorized) return "start";
  if (job.status === "estimate" || job.status === "scheduled") return "start";
  /* IN THE ORDER THE STEPS ARE IN, which sounds obvious and was not:
     this returned "car" before "after" while the rail ran the other way,
     so resuming a job could land you behind where you had got to. */
  /* A JOB ALREADY UNDER WAY IS NOT MISSING ITS ARRIVAL PHOTO, it is past
     the moment when one could be taken. Sending a technician back to the
     start of a car that has been in pieces since Friday, to photograph how
     it turned up, is a funnel arguing with the shop. Only a job being
     STARTED is held for it. */
  if (job.status !== "in_progress" && !(r.shot["arrival"] ?? 0)) return "start";
  if (!r.found) return "found";
  if (!(r.shot["release"] ?? 0)) return "after";
  if (!r.identified) return "car";
  return "done";
}

/* ── the panel ────────────────────────────────────────────────────── */

export async function techPage(jobId: string, role: Role): Promise<void> {
  mount(`<div class="wrap"><div class="loading">Opening the job&hellip;</div></div>`);

  const job = await getJob(jobId);
  if (!job) {
    mount(`<div class="wrap"><div class="empty">That job is not here.</div></div>`);
    return;
  }
  const lines = await listLines(jobId).catch(() => []);
  let ready = await readiness(job, lines);
  let at: StepKey = firstUnfinished(job, ready);

  const draw = async (): Promise<void> => {
    const i = stepAt(at);
    const step = stepWords(STEPS[i]!);
    const car = vehicleName(job.vehicles) || "the car";

    mount(`
      <div class="wrap techwrap">
        <div class="techhead">
          <a class="techback" href="#/job/${esc(job.id)}">&larr; The whole job</a>
          <div class="techcar">${esc(job.job_number)} &middot; ${esc(car)}</div>
        </div>

        <!-- The rail is the funnel, and it is named. A technician can
             see what is coming, not merely how much of it. -->
        <div class="techrail">
          ${STAGES.map((g, n) => {
            const here = n === stageOf(at);
            const past = n < stageOf(at);
            return `<span class="techstage ${past ? "done" : here ? "on" : ""}">
              <i></i><b>${esc(g.name)}</b></span>`;
          }).join("")}
        </div>

        <div class="card pad techpanel">
          <div class="techstep">Step ${i + 1} of ${STEPS.length}</div>
          <h2 class="techtitle">${esc(step.title)}</h2>
          <p class="techsub">${esc(step.sub)}</p>
          <div id="techbody"></div>
          <div class="techacts" id="techacts"></div>
          <div class="msg-slot" id="techmsg"></div>
        </div>
      </div>`);

    await body(step);
  };

  /** Move on, re-reading the job so a step cannot pass on stale facts. */
  const go = async (k: StepKey): Promise<void> => {
    at = k;
    const fresh = await getJob(jobId);
    if (fresh) Object.assign(job, fresh);
    ready = await readiness(job, await listLines(jobId).catch(() => lines));
    await draw();
  };

  const next = (): StepKey => STEPS[Math.min(stepAt(at) + 1, STEPS.length - 1)]!.key;
  const say = (html: string): void => {
    const m = document.getElementById("techmsg");
    if (m) m.innerHTML = html;
  };

  /* ── each step's body and its buttons ───────────────────────────── */

  const body = async (step: Step): Promise<void> => {
    const host = document.getElementById("techbody");
    const acts = document.getElementById("techacts");
    if (!host || !acts) return;

    const onward = (label = "Next") =>
      `<button class="btn primary wide" id="tnext">${esc(label)}</button>`;
    const back = stepAt(at) > 0
      ? `<button class="btn ghost" id="tback">Back</button>` : "";

    switch (step.key) {
      /* Phase 8. The rule is the whole step. */
      case "start": {
        /* A WARRANTY JOB HAS NOBODY TO ASK. The authorization gate below
           exists so a customer agrees to a figure before work starts. On a
           job the shop has decided not to bill there is no figure, so the
           gate would be asking for a signature on nothing. The decision is
           already on the job, with who made it and why. */
        if (job.no_charge_reason) {
          host.innerHTML = `
            <div class="techok">
              <b>No charge &mdash; ${esc(job.no_charge_reason)}</b>
              <p>Nobody is being billed for this one, so there is no price to
              agree to. Do the work and close it the same way.</p>
            </div>
            ${readingField()}
            <div class="techshoot">
              <div class="techshoot-t">One photo of how it turned up${
                (ready.shot["arrival"] ?? 0) || job.status === "in_progress"
                  ? "" : " — required to start"}</div>
              <div id="techshots"></div>
            </div>`;
          jobPhotos("techshots", job.id, job.vehicles?.id ?? null, role, "arrival");
          acts.innerHTML = back + onward(
            job.status === "in_progress" ? "Carry on" : "Start the job");
          wireStart();
          return;
        }

        if (!ready.authorized) {
          /**
           * THE RULE STAYS. THE DEAD END GOES.
           *
           * This said "the desk has to get a yes on the price first" and
           * offered one button, back to the job. In a shop with a desk
           * that is the correct hand-off. For the shops on this system it
           * is a wall: Matt Bolen IS the desk, and the screen that records
           * a spoken yes lives somewhere else entirely -- a separate
           * Authorizations page he would have to know exists, leave the
           * car for, and find.
           *
           * So the same form the job card uses is mounted right here. The
           * protocol is untouched: no work starts without an
           * authorization on file. What changes is that a person who HAS
           * the yes, on the phone, standing at the car, can write it down
           * in the place they are standing rather than abandoning the walk
           * to go looking for the right screen.
           *
           * The figure is prefilled from what is actually priced on the
           * job, because the number they agreed to is the number in front
           * of them, and retyping it is how it ends up wrong.
           */
          host.innerHTML = `
            <div class="techstop">
              <b>Nobody has said yes yet.</b>
              <p>No work starts without it. If they have already agreed on
              the phone or in person, write it down here and carry on.</p>
            </div>
            <div id="techauth"></div>`;
          authForm("techauth", job.id, ready.priced,
            job.customers?.full_name ?? "",
            () => { void (async () => {
              const fresh = await listLines(job.id).catch(() => []);
              ready = await readiness(job, fresh);
              await draw();
            })(); });
          acts.innerHTML = `<a class="btn ghost" href="#/job/${esc(job.id)}">Open the whole job</a>`;
          return;
        }
        host.innerHTML = `
          <div class="techok">
            <b>Approved &mdash; ${esc(money(ready.approvedFigure))}</b>
            <p>That is the figure the customer agreed to. If the repair turns
            out bigger than this, you stop at step 4 and the desk gets a new yes.</p>
          </div>
          ${readingField()}
          <div class="techshoot">
            <div class="techshoot-t">One photo of how it turned up${
              (ready.shot["arrival"] ?? 0) || job.status === "in_progress"
                ? "" : " — required to start"}</div>
            ${job.status === "in_progress" && !(ready.shot["arrival"] ?? 0)
              ? `<p class="techfine">This one was already under way, so it is not
                 held for a photo. Take one anyway if the car still looks like
                 it did when it arrived.</p>` : ""}
            <div id="techshots"></div>
          </div>`;
        jobPhotos("techshots", job.id, job.vehicles?.id ?? null, role, "arrival");
        acts.innerHTML = back + onward(
          job.status === "in_progress" ? "Carry on" : "Start the job");
        /* One wiring for both ways into this step: the ordinary job
           with an approved figure, and the warranty job with none. */
        function wireStart(): void {
          const jb = job!;
        document.getElementById("tnext")?.addEventListener("click", async () => {
            const b = document.getElementById("tnext") as HTMLButtonElement;
            b.disabled = true; b.textContent = "Starting…";
            try {
              /* NEVER DISABLE THE WAY FORWARD.
                 The first version greyed this out until ready.shot said a
                 photo existed — and ready.shot was read when the panel drew.
                 jobPhotos has no way to tell us it uploaded one, so the
                 button stayed dead after the photo landed and the funnel had
                 no exit. Anthony: "I can't get past the photo step."
                 So it is always pressable and the check happens here, on a
                 fresh read. A blocked step must always say why. */
              const now = await photosForJob(jb.id).catch(() => []);
              if (jb.status !== "in_progress" && !now.some((p) => p.kind === "arrival")) {
                b.disabled = false;
                // Only reachable when the job has not started, so the label is not a choice.
                b.textContent = "Start the job";
                say(`<p class="msg bad">One photo of the ${esc(shopWords().thing)} as it
                  is now, then you can start. It is the record that settles an
                  argument later.</p>`);
                return;
              }
              const miles = (document.getElementById("todo") as HTMLInputElement)
                ?.value.replace(/\D/g, "") ?? "";
              if (miles) await stamp(`Odometer ${Number(miles).toLocaleString("en-US")} miles.`);
              if (jb.status !== "in_progress") {
                await setStatus(jb.id, "in_progress",
                  { hasAuthorization: ready.authorized,
                    hasPricedLine: lines.some((l) => Number(l.total) > 0),
                    noCharge: Boolean(jb.no_charge_reason) });
              }
              await go("inspect");
            } catch (e) {
              b.disabled = false; b.textContent = "Start the job";
              say(`<p class="msg bad">${esc(msg(e))}</p>`);
            }
          });
        }
        wireStart();
        break;
      }

      /* Phases 9, 13, 14 all collect photos; only the kind and the bar differ. */
      case "repair":
      case "after": {
        const kind = step.shoots!;
        const have = ready.shot[kind] ?? 0;
        const must = step.key !== "repair";
        host.innerHTML = `
          <div id="techshots"></div>
          ${must && !have
            ? `<p class="techneed">At least one photo before you move on.</p>`
            : `<p class="techhave">${have} on this job so far.</p>`}`;
        jobPhotos("techshots", job.id, job.vehicles?.id ?? null, role, kind);
        /* NOT DISABLED. The same trap the start step had: ready.shot is
           read when the panel draws, jobPhotos cannot say it uploaded one,
           so a greyed button stays gray after the photo lands. Always
           pressable, checked here against a fresh read. */
        acts.innerHTML = back + onward("Next");
        document.getElementById("tnext")?.addEventListener("click", async () => {
          const b2 = document.getElementById("tnext") as HTMLButtonElement;
          if (must) {
            b2.disabled = true; b2.textContent = "Checking…";
            const now = await photosForJob(job.id).catch(() => []);
            if (!now.some((p) => p.kind === kind)) {
              b2.disabled = false; b2.textContent = "Next";
              say(`<p class="msg bad">One photo of the finished work first.
                It is the one the customer opens their page to see.</p>`);
              return;
            }
          }
          await go(next());
        });
        break;
      }

      /* Phase 9. The inspection screen is already built and good, so this
         step sends them to it rather than growing a second one here. The
         panel resumes from the job when they come back. */
      case "inspect": {
        host.innerHTML = `
          <p class="techfine">Walk the ${esc(shopWords().thing)} before anything comes apart. The
          inspection is its own screen because it is long. This step is here
          so it does not get skipped.</p>`;
        acts.innerHTML = back
          + `<a class="btn" href="#/inspection/${esc(job.id)}">Open the inspection</a>`
          + `<button class="btn primary" id="tnext">Done, next</button>`;
        document.getElementById("tnext")?.addEventListener("click", () => void go("found"));
        break;
      }

      /* Phase 10. ONE BOX IN THEIR OWN WORDS, AND THE DAMAGE PHOTOS.

         This step used to be a list of tests, each with Passed / Found the
         fault against it, an AI planner that proposed the list, and a rule
         that would not let anybody past until a verdict was marked.
         Anthony, 21 September: "it's confusing and redundant." It is gone
         whole -- see data/tests.ts for what survived it and why.

         What the desk needs off this step never was the verdicts. It was
         one sentence saying what is wrong with the car, which is what it
         quotes from. So that is all this asks for now. */
      case "found": {
        /* Prefilled from the job rather than from anything this panel
           remembers, so a technician who wrote it up and tapped Back gets
           their own words rather than an empty box.

           THE WHOLE CARD, NOT ITS FIRST LINE. This used to read the tagged
           line out of notes up to the first line break, so a diagnosis
           written as two lines came back as one and saving it again cut
           the second line off for good. Since 0188 it is the card's own
           column and it comes back whole. */
        const already = diagnosisOf(job);

        host.innerHTML = `
          <label class="techfield"><span>What did you find?</span>
            <textarea id="tfound" rows="5" placeholder="What is wrong with the ${
              esc(shopWords().thing)}, in your own words.">${esc(already)}</textarea></label>
          <p class="techfine">This is the diagnostic card the desk quotes from
          when it prices the work, so say what is wrong rather than what you
          did to look. You can add to it later from the job.
          <a href="#/diagnosis/${esc(job.id)}">Open the diagnostic card</a></p>
          <div id="techshots"></div>`;
        jobPhotos("techshots", job.id, job.vehicles?.id ?? null, role, "damage");

        acts.innerHTML = back + onward("Send it to the desk");
        document.getElementById("tnext")?.addEventListener("click", async () => {
          const said = (document.getElementById("tfound") as HTMLTextAreaElement)
            .value.trim();
          /* The one thing still held for. Not ceremony: the step is what
             puts a diagnosis on the job at all, and letting it through
             empty leaves the desk pricing off the complaint alone. */
          if (!said) {
            say(`<p class="msg bad">Say what you found first. That is the bit
              the desk quotes from.</p>`);
            return;
          }
          const b = document.getElementById("tnext") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Sending…";
          try {
            /* Onto the card, not into the notes. The notes stay the
               shared jacket for everybody else's lines. */
            const saved = await saveDiagnosis(job.id, said);
            job.diagnosis = saved.diagnosis;
            job.diagnosed_at = saved.diagnosed_at;
            job.diagnosed_by = saved.diagnosed_by;
            await go("money");
          } catch (e) {
            b.disabled = false; b.textContent = "Send it to the desk";
            say(`<p class="msg bad">${esc(msg(e))}</p>`);
          }
        });
        break;
      }

      /* Phase 11. The only step whose good outcome may be stopping. */
      case "money": {
        host.innerHTML = `
          <div class="techsums">
            <div><span>They approved</span><b>${esc(money(ready.approvedFigure))}</b></div>
            <div><span>On the job now</span><b>${esc(money(ready.priced))}</b></div>
          </div>
          <p class="techfine">If what you have found needs more than the approved
          figure, the customer has to say yes to the new number before a wrench
          moves. Telling the desk is the whole of your job here.</p>`;
        acts.innerHTML = `
          ${back}
          <button class="btn ghost danger" id="tstop">It needs more than that</button>
          <button class="btn primary" id="tnext">It is inside the quote</button>`;
        /* STOPPING IS NOT THE SAME AS WAITING. The rule is that work does
           not pass the approved figure without a new yes — not that the
           technician has to find an advisor. Whoever is standing at the car
           can price it and send it, and on a mobile job that is the only
           person there. */
        document.getElementById("tnext")?.addEventListener("click", () => void go("repair"));
        document.getElementById("tstop")?.addEventListener("click", async () => {
          const b = document.getElementById("tstop") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Telling the desk…";
          try {
            await stamp("Tech stopped: the repair is bigger than the approved "
              + "figure. Needs a new quote and a new yes before work carries on.");
            host.innerHTML = `
              <div class="techstop">
                <b>Stopped, and the desk knows.</b>
                <p>It is on the job. Do not carry on until the customer has said
                yes to the new number.</p>
              </div>`;
            const a = document.getElementById("techacts");
            if (a) {
              a.innerHTML = `
                <a class="btn primary" href="#/job/${esc(job.id)}/work">Price it and send it</a>
                <a class="btn ghost" href="#/jobs">Leave it for the desk</a>`;
            }
          } catch (e) {
            b.disabled = false; b.textContent = "It needs more than that";
            say(`<p class="msg bad">${esc(msg(e))}</p>`);
          }
        });
        break;
      }

      /* Phase 15. The database refuses a close without this, so ask for it
         here rather than letting the desk hit the wall at invoicing. */
      /**
       * ─── THE STEP ASKED FOR A PLATE THAT COULD NOT CLEAR IT ──────────
       *
       * Anthony, 2026-09-18: "they still have vehicle or vin on their
       * start job tab. Change to location or something that fits their
       * business" — "for just their trade in shops".
       *
       * The HEADING here has been trade-aware since stepWords() (it reads
       * "The case" for a private investigator). The two BOXES under it
       * said Number plate and or the VIN, so an investigator was asked
       * for a plate and a VIN underneath a heading about a case.
       *
       * AND IT WAS A DEAD END, NOT ONLY WRONG WORDS. isIdentified() only
       * looks at license_plate when the trade hasPlate. For the four that
       * do not — investigations, locksmith, garage door, headlight — a
       * plate typed here is written to the record and the gate still
       * reads false, so Save it did not advance and there was nothing on
       * the screen to explain why. What actually clears it for those
       * trades is the serial, or the make and model together.
       *
       * So the boxes are now the ones that can satisfy the gate, per
       * trade. A garage keeps Number plate and or the VIN exactly as it
       * was; a private investigator gets Case number, Matter and Work.
       */
      case "car": {
        const v = job.vehicles;
        const w = shopWords();
        /* Which boxes, from the same module that judges them. Branching on
           hasPlate here instead would be a second copy of the rule, and a
           second copy is how the plate box outlived the gate that ignores
           it. See identifyAsks() in src/domain/trade.ts. */
        const asks = identifyAsks(currentTrade());
        const already = asks.plate
          ? (v?.license_plate ? `Plate ${v.license_plate}` : `${w.serial} ${v?.vin}`)
          : (v?.vin
            ? `${cap(w.serial)} ${v.vin}`
            : [v?.make, v?.model].filter(Boolean).join(" ") || (v?.description ?? ""));
        host.innerHTML = ready.identified
          ? `<div class="techok"><b>Already on file.</b>
               <p>${esc(already)}</p>
             </div>`
          : asks.plate
            ? `<label class="techfield"><span>Number plate</span>
                 <input id="tplate" autocomplete="off"></label>
               <label class="techfield"><span>or the ${esc(w.serial)}</span>
                 <input id="tvin" autocomplete="off" maxlength="17"></label>
               <p class="techfine">One or the other. Without it the job cannot be
               finished, invoiced or warrantied.${v?.id ? "" :
                 ` There is no ${esc(w.thing)} on this job yet, so this creates one.`}</p>`
            : `<label class="techfield"><span>${esc(cap(w.serial))}</span>
                 <input id="tvin" autocomplete="off"></label>
               <p class="techfine">or, if it has no ${esc(w.serial)} yet, both of these.</p>
               <label class="techfield"><span>${esc(w.makeLabel)}</span>
                 <input id="tmake" autocomplete="off" placeholder="${esc(w.makeExample)}"></label>
               <label class="techfield"><span>${esc(w.modelLabel)}</span>
                 <input id="tmodel" autocomplete="off" placeholder="${esc(w.modelExample)}"></label>
               <p class="techfine">Without one of them the job cannot be
               finished, invoiced or warrantied.${v?.id ? "" :
                 ` There is no ${esc(w.thing)} on this job yet, so this creates one.`}</p>`;
        acts.innerHTML = back + onward(ready.identified ? "Next" : "Save it");
        document.getElementById("tnext")?.addEventListener("click", async () => {
          if (ready.identified) { void go("done"); return; }
          const box = (x: string) =>
            (document.getElementById(x) as HTMLInputElement | null)?.value.trim() ?? "";
          /* A plate and a VIN are codes and are shouted. A matter and a
             work type are words somebody typed, and upper-casing those
             turns "Matrimonial surveillance" into shouting on the record. */
          const plate = box("tplate").toUpperCase();
          const vin = asks.plate ? box("tvin").toUpperCase() : box("tvin");
          const make = box("tmake");
          const model = box("tmodel");
          if (asks.plate ? (!plate && !vin) : (!vin && !(make && model))) {
            say(`<p class="msg bad">${esc(asks.plate
              ? "One of the two, then it can close."
              : `The ${w.serial}, or both the ${w.makeLabel.toLowerCase()} and the ${
                w.modelLabel.toLowerCase()}. Then it can close.`)}</p>`);
            return;
          }
          /* NO CAR ON THE JOB AT ALL — make one rather than stop.
             The smoke test found Q426 like this: scheduled, authorized,
             and no vehicle record, so a technician reached this step, typed
             the plate off the back of the car, and was told the desk had to
             do it. The person standing at the car IS the right person to
             create it, and refusing left the funnel with no exit. */
          const b = document.getElementById("tnext") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Saving…";
          try {
            if (v?.id) {
              if (plate) await updateVehicleField(v.id, "license_plate", plate);
              if (vin) await updateVehicleField(v.id, "vin", vin);
              /* Written in the same order the gate reads them, so a trade
                 with no plate can clear this on make and model alone. */
              if (make) await updateVehicleField(v.id, "make", make);
              if (model) await updateVehicleField(v.id, "model", model);
            } else {
              await attachVehicle(job.id, {
                year: null, make: make || null, model: model || null,
                license_plate: plate || null, vin: vin || null,
              });
              // The panel's copy of the job predates the car; re-read so the
              // next step does not still think there is nothing to identify.
              const fresh = await getJob(job.id);
              if (fresh) Object.assign(job, fresh);
            }
            await go("done");
          } catch (e) {
            b.disabled = false; b.textContent = "Save it";
            say(`<p class="msg bad">${esc(msg(e))}</p>`);
          }
        });
        break;
      }

      /* Handover. Finishing is a claim about the work, not about the money,
         so this stops at completed and leaves invoicing to the desk. */
      case "done": {
        const missing: string[] = [];
        if (!(ready.shot["arrival"] ?? 0)) missing.push("photos from before you started");
        if (!(ready.shot["release"] ?? 0)) missing.push("photos of the finished work");
        if (!ready.identified) {
          /* A door has neither. isIdentified() decides what counts;
             this only has to say the same thing in words — including the
             second way a no-plate trade can satisfy it, which this used
             to leave out entirely. */
          const w2 = shopWords();
          missing.push(w2.hasPlate
            ? `a plate or a ${w2.serial}`
            : `a ${w2.serial}, or the ${w2.makeLabel.toLowerCase()} and ${
              w2.modelLabel.toLowerCase()}`);
        }
        if (!hasDiagnosis(job)) missing.push("what you found");

        host.innerHTML = missing.length
          ? `<div class="techstop"><b>Not finished yet.</b>
               <p>Still owed: ${esc(missing.join(", "))}.</p></div>`
          : `<div class="techok"><b>That is everything.</b>
               <p>Mark it finished and the desk will invoice it. ${
                 /* "until it drives away" is a car sentence. A case does not
                    drive anywhere, and the point being made is about the
                    handover, not the wheels. */
                 esc(shopWords().hasEngine
                   ? "The car is still your problem until it drives away."
                   : `The ${shopWords().thing} is still yours until the desk has it.`)
               }</p></div>`;

        acts.innerHTML = back + (missing.length
          ? `<button class="btn" id="tfix">Go back and finish it</button>`
          : `<button class="btn primary wide" id="tdone">Finished</button>`);

        document.getElementById("tfix")?.addEventListener("click", () =>
          void go(firstUnfinished(job, ready)));

        document.getElementById("tdone")?.addEventListener("click", async () => {
          const b = document.getElementById("tdone") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Finishing…";
          try {
            if (job.status !== "completed") {
              await setStatus(job.id, "completed",
                { hasAuthorization: ready.authorized,
                  hasPricedLine: lines.some((l) => Number(l.total) > 0),
                  noCharge: Boolean(job.no_charge_reason) });
            }
            await go("bill");
          } catch (e) {
            b.disabled = false; b.textContent = "Finished";
            say(`<p class="msg bad">${esc(msg(e))}</p>`);
          }
        });
        break;
      }

      /* Phase 16. The figure is never typed — it comes off the lines, so
         the bill cannot disagree with what the customer approved. */
      case "bill": {
        if (job.no_charge_reason) {
          host.innerHTML = `<div class="techok">
            <b>Nothing to bill.</b>
            <p>This one is ${esc(job.no_charge_reason.toLowerCase())}. There is
            no invoice and nothing to collect, so the job is finished.</p></div>`;
          acts.innerHTML = back + `<a class="btn primary wide" href="#/jobs">Done</a>`;
          return;
        }
        const m = await moneyFor(job.id, ready.priced).catch(() => null);
        const raised = Boolean(m?.invoiceId);
        const billed = Number(m?.total ?? 0);
        host.innerHTML = raised
          ? `<div class="techok"><b>Already billed &mdash; ${esc(money(billed))}</b>
               <p>The invoice is raised. Next is the money.</p></div>`
          : `<div class="techsums">
               <div><span>The work</span><b>${esc(money(ready.priced))}</b></div>
             </div>
             <p class="techfine">Raising the invoice does not send anything to the
             customer. It is the bill existing, so it can be paid.</p>`;
        acts.innerHTML = back + onward(raised ? "Next" : "Raise the invoice");
        document.getElementById("tnext")?.addEventListener("click", async () => {
          if (raised) { void go("paid"); return; }
          const b = document.getElementById("tnext") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Raising…";
          try {
            await raiseInvoice(job.id);
            await go("paid");
          } catch (e) {
            b.disabled = false; b.textContent = "Raise the invoice";
            say(`<p class="msg bad">${esc(msg(e))}</p>`);
          }
        });
        break;
      }

      /* Phases 17 and 18. Cash written up properly is the difference
         between a real revenue figure and a guess, and the person holding
         the notes is the one standing at the car. */
      case "paid": {
        const m = await moneyFor(job.id, ready.priced).catch(() => null);
        const owed = m ? Number(m.balance) : ready.priced;
        if (!(owed > 0)) {
          host.innerHTML = `<div class="techok"><b>Settled.</b>
            <p>Nothing outstanding. Keys back and it is finished.</p></div>`;
          acts.innerHTML = `<a class="btn primary wide" href="#/jobs">Done</a>`;
          return;
        }
        host.innerHTML = `
          <div class="techsums"><div><span>Outstanding</span>
            <b>${esc(money(owed))}</b></div></div>
          <label class="techfield"><span>How much</span>
            <input id="tamt" inputmode="decimal" value="${owed.toFixed(2)}"></label>
          <label class="techfield"><span>How they paid</span>
            <select id="tmethod">${(Object.keys(METHOD_LABEL) as Method[])
              .map((k) => `<option value="${esc(k)}">${esc(METHOD_LABEL[k])}</option>`)
              .join("")}</select></label>
          <label class="techfield"><span>Reference, if there is one</span>
            <input id="tref" autocomplete="off"></label>`;
        acts.innerHTML = back
          + `<button class="btn primary" id="tnext">Record it</button>`
          + `<a class="btn ghost" href="#/jobs">They are paying the desk</a>`;
        document.getElementById("tnext")?.addEventListener("click", async () => {
          const amt = Number((document.getElementById("tamt") as HTMLInputElement).value);
          if (!(amt > 0)) { say(`<p class="msg bad">How much did they pay?</p>`); return; }
          const b = document.getElementById("tnext") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Recording…";
          try {
            await recordPayment(job.id, amt,
              (document.getElementById("tmethod") as HTMLSelectElement).value as Method,
              (document.getElementById("tref") as HTMLInputElement).value.trim() || undefined);
            await go("paid");
          } catch (e) {
            b.disabled = false; b.textContent = "Record it";
            say(`<p class="msg bad">${esc(msg(e))}</p>`);
          }
        });
        break;
      }
    }

    document.getElementById("tback")?.addEventListener("click", () =>
      void go(STEPS[Math.max(stepAt(at) - 1, 0)]!.key));
  };

  /**
   * A LINE ON THE JOB, NEVER A REPLACEMENT.
   *
   * The notes field belongs to everyone who touches the job. Overwriting
   * it would lose the advisor's line to gain the technician's, so this
   * appends with the date on it, the same way a paper jacket accretes.
   */
  const stamp = async (line: string): Promise<void> => {
    const when = new Date().toLocaleDateString("en-US",
      { month: "short", day: "numeric" });
    const before = String(job.notes ?? "").trim();
    const next = `${before}${before ? "\n\n" : ""}${when} — ${line}`;
    await updateJobField(job.id, "notes", next);
    job.notes = next;
  };

  await draw();
}

const msg = (e: unknown): string => (e instanceof Error ? e.message : String(e));
